import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {dialog, registerEvent} from '../../electron-modules';
import {store} from '../../redux/store';
import {
  Asset,
  AssetType,
  getCameraFileAssets,
  getLandXMLAssets,
  getModelAssets,
  getOrthoAssets,
  getPointCloudAssets,
  selectAllAssets
} from '../../redux/assets-slice';
import {trackToolUsage} from '../../executable';
import {useSelector} from 'react-redux';
import {
  Button,
  CircularProgress,
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  ListItem,
  makeStyles,
  Theme,
  Typography,
  useTheme
} from '@material-ui/core';
import {
  ArrowTooltip,
  DenseDivider,
  EnhancedTable,
  LinearProgressWithLabel,
  SimpleDialog,
  TypographyLink
} from '../../components';
import sanitize from "sanitize-filename";
import {selectProjectID, selectProjectName} from '../../redux/project-slice';
import {toast} from '../../app';
import WarningIcon from '@material-ui/icons/Warning';
import {useTranslation} from 'react-i18next';
import {useAuth, useDialog} from "../../hooks";
import {getCloudExporter} from "./exporter";
import {getLocalizedProjectName} from '../../utilities';
import {throttle} from 'throttle-debounce';
import {getLocalizedURL} from '../../localization';
import { frontendHost } from '../../urls';
import { useTableState } from '../../hooks/use-table-state';

interface AssetStatus {
  filename: string;
  status: string;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    pointWarning: {
      color: theme.palette.warning.main,
      marginBottom: "-5px",
      paddingLeft: "5px"
    },
    contentCenter: {
      textAlign: "center"
    },
    cancelProgress: {
      marginRight: theme.spacing(1)
    },
    cancelContent: {
      paddingBottom: theme.spacing(3)
    },
    bulletListItem: {
      display: 'list-item',
      padding: theme.spacing(0.5, 1)
    }
  }),
);

interface LinkedProjectProps {
  name: string
}

interface NewProjectProps {
  name: string
  numRawPointCloud: number
  pointClouds: Asset[]
  camerasFiles: Asset[]
  orthoFiles: Asset[]
  modelFiles: Asset[]
  landXMLFiles: Asset[]
}

const LinkedProjectInfo = (props: LinkedProjectProps) => {
  const {name} = props;

  const {t} = useTranslation();

  return (
    <React.Fragment>
      <Typography dangerouslySetInnerHTML={{
        __html: t("export.existing_project_updated", {
          name: name
        })
      }}/>
    </React.Fragment>
  );
};

const NewProjectInfo = (props: NewProjectProps) => {
  const {name, pointClouds, camerasFiles, orthoFiles,
    modelFiles, landXMLFiles, numRawPointCloud} = props;

  const theme = useTheme();
  const classes = useStyles(theme);
  const {t} = useTranslation();

  return (
    <React.Fragment>
      <Typography dangerouslySetInnerHTML={{
        __html: t("export.new_project_will_be_created", {
          name: name
        })
      }}/>

      {(pointClouds.length > 0) && (
        <ListItem className={classes.bulletListItem}>
          {t("export.asset-points", {
            count: pointClouds.length
          })}

          {(numRawPointCloud > 0) && (
            <ArrowTooltip title={t('export.this-process-may-take-some-time')}>
              <WarningIcon className={classes.pointWarning} fontSize="small"/>
            </ArrowTooltip>
          )}
        </ListItem>
      )}

      {(camerasFiles.length > 0) && (
        <ListItem className={classes.bulletListItem}>
          {t("export.asset-camera", {
            count: camerasFiles.length
          })}
        </ListItem>
      )}

      {(orthoFiles.length > 0) && (
        <ListItem className={classes.bulletListItem}>
          {t("export.asset-orthos", {
            count: orthoFiles.length
          })}
        </ListItem>
      )}

      {(modelFiles.length > 0) && (
        <ListItem className={classes.bulletListItem}>
          {t("export.asset-models", {
            count: modelFiles.length
          })}
        </ListItem>
      )}

      {(landXMLFiles.length > 0) && (
        <ListItem className={classes.bulletListItem}>
          {t("export.asset-landxml", {
            count: landXMLFiles.length
          })}
        </ListItem>
      )}

    </React.Fragment>
  );
};

export const CloudExporter = () => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const {i18n, t} = useTranslation();

  const tableState = useTableState({
    defaultSort: "status",
    rowPropertyMap: {status: "status_code"}
  });

  const exporterDialog = useDialog();
  const messageDialog = useDialog();
  const cancelDialog = useDialog();

  const projectID = useSelector(selectProjectID);
  const projectName = useSelector(selectProjectName);
  const assets = useSelector(selectAllAssets);
  const orthoFiles = getOrthoAssets(assets);
  const pointClouds = getPointCloudAssets(assets);
  const camerasFiles = getCameraFileAssets(assets);
  const modelFiles = getModelAssets(assets);
  const landXMLFiles = getLandXMLAssets(assets);

  const [assetStatuses, setAssetStatuses] = useState<AssetStatus[]>([]);
  const [exportPercent, updateExportPercent] = useState(0);
  const [isExportRunning, setIsExportRunning] = useState(false);
  const [isExportFinished, setIsExportFinished] = useState(false);
  const [projectURL, setProjectURL] = useState("");

  const {user, checkProject, logIn, isLoggedIn} = useAuth();
  const [startCloudExport, cancelCloudExport] = useMemo(getCloudExporter, []);

  const progressThrottleMilliseconds = 100;

  const setExportPercent = useCallback(throttle(
    progressThrottleMilliseconds, (value) => {
      updateExportPercent(value);
    }), []);

  const canExport = (pointClouds.length + camerasFiles.length
    + orthoFiles.length + modelFiles.length) > 0;

  const numRawPointCloud = pointClouds.filter(asset => {
    return (asset.type === AssetType.LAS)
      || (asset.type === AssetType.E57Points);
  }).length;

  const getRows = () => {
    const getColor = (status: string) => {
      switch (status) {
      case "uploaded":
        return theme.palette.success.main;
      case "uploading":
      case "preparing":
        return theme.palette.info.main;
      case "errored":
        return theme.palette.error.main;
      case "queued":
      default:
        return theme.palette.primary.main;
      }
    };

    return assetStatuses.map((assetStatus, index) => ({
      ...assetStatus,
      status: (
        <span style={{color: getColor(assetStatus.status)}}>
          {t(`export.${assetStatus.status}`)}
        </span>
      ),
      id: `UploadTableRow_${index}`,
    }));
  };

  const reset = () => {
    setExportPercent(0);
    setAssetStatuses([]);
    setIsExportFinished(false);
    setProjectURL("");
  };

  const onExportFinished = () => {
    setIsExportRunning(false);
    setIsExportFinished(true);
  };

  const handleExportCancel = () => {
    reset();
    messageDialog.handleClose();
    cancelCloudExport(user.token);
  };

  const handleExport = () => {
    exporterDialog.handleClose();
    exportToCloud();
  };

  const updateStatuses = async (statuses) => {
    setAssetStatuses(statuses);
  };

  const exportToCloud = async() => {
    trackToolUsage("exporter");

    reset();
    messageDialog.handleOpen();

    const assets = [
      ...pointClouds,
      ...camerasFiles,
      ...modelFiles
    ];

    await startCloudExport({
      name: exportName,
      assets: assets,
      state: store.getState(),
      token: user.token,
      onUpdate: updateStatuses,
      onProgress: setExportPercent,
      onError: (title, message) => {
        onExportFinished();
        dialog.showErrorBox(title, message);
      },
      onFinish: (projectID) => {
        setProjectURL(getLocalizedURL(
          `${frontendHost}/viewer/${projectID}`, i18n.language));
        onExportFinished();
      }
    });
  };

  useEffect(() => {
    registerEvent("open-cloud-export", async () => {
      if (!canExport) return toast.error(t("toast.no-data-loaded"));

      if (!isLoggedIn) {
        const success = await logIn();
        if (!success) return;
      }

      const linked = await checkProject(projectID);
      exporterDialog.handleOpen(linked);
    });
  }, [isLoggedIn, projectID, canExport]);

  const localizedProjectName = getLocalizedProjectName(projectName);
  const sanitizedName = sanitize(localizedProjectName);
  const exportName = t('export.export-name', {name: sanitizedName});
  const isLinkedProject = !!exporterDialog.data;

  const columns = [
    {
      id: 'filename',
      label: t("export.filename")
    },
    {
      id: 'status',
      label: t("export.status")
    }
  ];

  const rows = getRows();

  return (
    <React.Fragment>
      {/* Exporting dialog */}
      <Dialog
        open={messageDialog.open}
        fullWidth
        scroll="body"
        maxWidth="sm"
      >
        <DialogTitle>{t('export.export-status')}</DialogTitle>

        <DialogContent className={classes.contentCenter}>
          {isExportFinished && (
            <>
              <Typography variant="body1">
                { assetStatuses.some(status => status.status === "errored")
                  ? t("export.project_uploaded_with_errors")
                  : t("export.project-uploaded-successfully")
                }
              </Typography>

              <TypographyLink
                variant="caption"
                text={projectURL}
                url={projectURL}
              />

              <DenseDivider variant="middle" spacing={10}/>
            </>
          )}

          <LinearProgressWithLabel value={exportPercent} color="secondary"/>

          <EnhancedTable
            state={tableState}
            columns={columns}
            rows={rows}
            maxHeight={"300px"}
          />
        </DialogContent>

        <DialogActions>
          {!isExportFinished && (
            <Button
              onClick={handleExportCancel}
              color="primary"
              disabled={isExportRunning}
            >
              {t('export.cancel-export')}
            </Button>
          )}

          {isExportFinished && (
            <Button
              onClick={messageDialog.handleClose}
              color="primary"
            >
              {t("export.close")}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Main dialog */}
      <SimpleDialog
        open={exporterDialog.open}
        onClose={exporterDialog.handleClose}
        onSubmit={handleExport}
        maxWidth={"xs"}
        title={t('export.data-export')}
        button={t("buttons.export")}
      >
        <Grid container spacing={2}>

          {/* Export file type(s) info */}
          <Grid item xs={12}>

            {/** Existing cloud project */}
            {isLinkedProject && <LinkedProjectInfo
              name={sanitizedName}
            />}

            {/** New cloud project */}
            {!isLinkedProject && <NewProjectInfo
              name={sanitizedName}
              pointClouds={pointClouds}
              camerasFiles={camerasFiles}
              orthoFiles={orthoFiles}
              modelFiles={modelFiles}
              landXMLFiles={landXMLFiles}
              numRawPointCloud={numRawPointCloud}
            />}
          </Grid>
        </Grid>
      </SimpleDialog>

      {/* Cancelling dialog */}
      <Dialog
        open={cancelDialog.open}
      >
        <DialogTitle>{t('export.data-export')}</DialogTitle>
        <DialogContent className={classes.cancelContent}>
          <CircularProgress size="1em" className={classes.cancelProgress}/>
          <DialogContentText display="inline">
            {t('export.cancelling-export')}
          </DialogContentText>
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
};